import { Col, Input } from 'antd';
import get from 'lodash/get';
import React, { memo } from 'react';

import ContactSummary from 'components/composites/ContactSummary';
import DefaultFormItem from 'components/composites/DefaultFormItem';
import FieldsValue from 'components/forms/FieldsValue';
import FormItemCol from 'components/forms/FormItemCol';
import FormItemEmail from 'components/forms/FormItemEmail';
import FormItemInputPhoneNumber from 'components/forms/FormItemInputPhoneNumber';
import useTranslation from 'hooks/useTranslation';
import User from 'types/models/user';

interface Props {
  namePrefix?: string;
  /** Use responsive column width */
  isFullSpanOnMobileHalfOnDesktop?: boolean;
  readOnly?: boolean;
  phoneRequired?: boolean;
}

/**
 * A reusable set of form items to be used within an antd Form element.
 * It assumes that the address being edited conforms to the standard user model.
 */
function ContactFormItems({
  isFullSpanOnMobileHalfOnDesktop,
  readOnly,
  phoneRequired = true
}: Props) {
  const { t } = useTranslation();

  const mobileNumberLabel = t('MOBILE_NUMBER');

  const firstNameFieldName = 'firstName';
  const lastNameFieldName = 'lastName';
  const mobileNumberFieldName = 'mobileNumber';
  const mobileNumberCountryCodeFieldName = 'mobileNumberCountryCode';
  const mobileNumberFormattedFieldName = 'mobileNumberFormatted';
  const landlineNumberFieldName = 'landlineNumber';
  const landlineNumberCountryCodeFieldName = 'landlineNumberCountryCode';
  const landlineNumberFormattedFieldName = 'landlineNumberFormatted';
  const emailFieldName = 'email';

  if (readOnly) {
    return (
      <Col span={24}>
        <DefaultFormItem label="Contact Details">
          <FieldsValue
            names={[
              firstNameFieldName,
              lastNameFieldName,
              mobileNumberFormattedFieldName,
              landlineNumberFormattedFieldName,
              emailFieldName
            ]}
          >
            {(fieldsValue) => {
              const firstName = get(fieldsValue, firstNameFieldName);
              const lastName = get(fieldsValue, lastNameFieldName);
              const mobileNumberFormatted = get(fieldsValue, mobileNumberFormattedFieldName);
              const landlineNumberFormatted = get(fieldsValue, landlineNumberFormattedFieldName);
              const email = get(fieldsValue, emailFieldName);

              const user: Partial<User> = {
                firstName: firstName as string | undefined,
                lastName: lastName as string | undefined,
                mobileNumberFormatted,
                landlineNumberFormatted,
                email: email as string | undefined
              };
              return <ContactSummary contact={user} />;
            }}
          </FieldsValue>
        </DefaultFormItem>
      </Col>
    );
  }

  return (
    <>
      <FormItemCol
        isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
      >
        <DefaultFormItem
          name={firstNameFieldName}
          label="First Name"
          rules={[
            {
              required: true,
              message: 'Please enter a first name'
            },
            {
              pattern: /^[A-Za-z ,.'-]+$/,
              message: 'First Name should not contain numbers.'
            }
          ]}
        >
          <Input />
        </DefaultFormItem>
      </FormItemCol>
      <FormItemCol
        isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
      >
        <DefaultFormItem
          name={lastNameFieldName}
          label="Last Name"
          rules={[
            {
              required: true,
              message: 'Please enter a last name'
            },
            {
              pattern: /^[A-Za-z ,.'-]+$/,
              message: 'Last Name should not contain numbers.'
            }
          ]}
        >
          <Input />
        </DefaultFormItem>
      </FormItemCol>
      <FormItemCol
        isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
      >
        <FormItemInputPhoneNumber
          name={mobileNumberFieldName}
          label={mobileNumberLabel}
          rules={[
            {
              required: phoneRequired,
              message: `Please enter a ${mobileNumberLabel.toLowerCase()}.`
            }
          ]}
          prefixName={mobileNumberCountryCodeFieldName}
        />
      </FormItemCol>
      <FormItemCol
        isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
      >
        <FormItemInputPhoneNumber
          name={landlineNumberFieldName}
          label="Landline Number"
          prefixName={landlineNumberCountryCodeFieldName}
        />
      </FormItemCol>
      <FormItemCol
        isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
      >
        <FormItemEmail />
      </FormItemCol>
    </>
  );
}

export default memo(ContactFormItems);
